import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import PageLayout from "@/components/layout/PageLayout";
import { Button } from "@/components/ui/button";
import { Helmet } from "react-helmet-async";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  ArrowRight,
  Check,
  FileText,
  Layers,
  Search,
  Settings2,
  Shield,
  Sparkles,
  Zap,
} from "lucide-react";
import { CoreIncluded } from "@/components/modules/CoreIncluded";
import { ModuleFilterBar } from "@/components/modules/ModuleFilterBar";
import { ModuleCard } from "@/components/modules/ModuleCard";
import {
  modules,
  MODULE_CATEGORY_LABELS,
  type ModuleCategory,
  calcUsedPoints,
} from "@/data/modules";
import { presets, getPresetPlan } from "@/data/presets";
import { formatPoints } from "@/data/plans";

const ALL_TAB = "all" as const;

// ── Example setups ───────────────────────────────────────────
const exampleSetups = [
  {
    label: "Solo-Starter",
    description: "Freelancer & Einzelunternehmer",
    presetId: "gesundheit_therapie",
    pointHint: "5 Punkte → Starter",
  },
  {
    label: "Service & Agentur",
    description: "Beratung, Marketing, IT-Dienstleistung",
    presetId: "agentur_beratung",
    pointHint: "10 Punkte → Professional",
  },
  {
    label: "Handel & E-Commerce",
    description: "Lager, Einkauf, Shop-Sync",
    presetId: "handel_ecom",
    pointHint: "16 Punkte → Business",
  },
];

const Features = () => {
  const [search, setSearch] = useState("");
  const [activeCategory, setActiveCategory] = useState<ModuleCategory | typeof ALL_TAB>(ALL_TAB);
  const [showSelectedOnly, setShowSelectedOnly] = useState(false);

  const filtered = useMemo(() => {
    return modules.filter((m) => {
      const matchesCat = activeCategory === ALL_TAB || m.category === activeCategory;
      const matchesSearch =
        search.trim() === "" ||
        m.name.toLowerCase().includes(search.toLowerCase()) ||
        m.shortDescription.toLowerCase().includes(search.toLowerCase()) ||
        m.tags.some((t) => t.toLowerCase().includes(search.toLowerCase()));
      return matchesCat && matchesSearch;
    });
  }, [search, activeCategory]);

  const grouped = useMemo(() => {
    if (activeCategory !== ALL_TAB) return { [activeCategory]: filtered };
    const result: Record<string, typeof filtered> = {};
    for (const cat of Object.keys(MODULE_CATEGORY_LABELS) as ModuleCategory[]) {
      const items = filtered.filter((m) => m.category === cat);
      if (items.length > 0) result[cat] = items;
    }
    return result;
  }, [filtered, activeCategory]);

  // FAQ
  const faqItems = [
    { question: "Was ist im Core enthalten?", answer: "Dokumentenmanagement (DMS), E-Mail & Versandkanal sowie Benutzer & Berechtigungen sind in jedem Plan inklusive – ohne Punkte." },
    { question: "Wie funktioniert das Punkte-System?", answer: "Jedes Modul kostet 1–3 Punkte. Ihr Plan gibt ein Punkte-Budget vor (Starter: 6, Professional: 12, Business: 20). Sie kombinieren frei innerhalb des Budgets." },
    { question: "Kann ich Module später ändern?", answer: "Ja, Sie können Module jederzeit hinzufügen oder entfernen. Falls Ihr Budget nicht reicht, upgraden Sie einfach den Plan." },
    { question: "Brauche ich eine Kreditkarte?", answer: "Nein, der erste Monat ist komplett kostenlos und ohne Zahlungsdaten nutzbar. Erst danach wird eine Zahlungsmethode benötigt." },
    { question: "Unterstützt NextInvoice E-Rechnungsformate?", answer: "Ja – XRechnung und ZUGFeRD sind im Rechnungsmodul integriert." },
  ];

  const softwareSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: "NextInvoice",
    applicationCategory: "BusinessApplication",
    operatingSystem: "Web",
    offers: { "@type": "Offer", price: "0", priceCurrency: "EUR", description: "1 Monat kostenlos testen" },
    description: "Modularer Baukasten für Geschäftsprozesse – Rechnungen, CRM, Lager, Personal und mehr.",
  };

  return (
    <PageLayout>
      <Helmet>
        <title>Alle Module im Überblick – NextInvoice</title>
        <meta name="description" content="18+ Module für Rechnungen, CRM, Lager, Personal, Buchhaltung und mehr. Kombinieren Sie frei nach dem Punkte-System. Jetzt kostenlos testen." />
        <link rel="canonical" href="https://nxtinvoice.de/features" />
        <script type="application/ld+json">{JSON.stringify(softwareSchema)}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "mainEntity": faqItems.map(faq => ({
            "@type": "Question",
            "name": faq.question,
            "acceptedAnswer": { "@type": "Answer", "text": faq.answer }
          }))
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            { "@type": "ListItem", "position": 1, "name": "Startseite", "item": "https://nxtinvoice.de/" },
            { "@type": "ListItem", "position": 2, "name": "Module" }
          ]
        })}</script>
      </Helmet>

      {/* Hero */}
      <section className="relative overflow-hidden bg-background py-20 lg:py-28">
        <div className="absolute inset-0 gradient-hero pattern-grid" />
        <div className="absolute right-0 top-20 h-96 w-96 rounded-full bg-accent/10 blur-3xl" />
        <div className="absolute -left-20 bottom-0 h-72 w-72 rounded-full bg-primary/10 blur-3xl" />

        <div className="container relative">
          <div className="mx-auto max-w-4xl text-center">
            <div className="mb-6 inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/5 px-4 py-2">
              <Layers className="h-4 w-4 text-primary" />
              <span className="text-sm font-semibold text-primary">Modul-Katalog</span>
            </div>

            <h1 className="mb-6 font-display text-4xl font-bold tracking-tight text-foreground sm:text-5xl lg:text-6xl">
              Alle Module{" "}
              <span className="text-primary">im Überblick</span>
            </h1>

            <p className="mx-auto mb-10 max-w-2xl text-lg leading-relaxed text-muted-foreground">
              Wählen Sie aus 18+ Modulen in 6 Kategorien – jedes Modul kostet 1–3 Punkte aus Ihrem Plan-Budget.
            </p>

            <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
              <Button size="lg" className="h-14 bg-accent px-8 text-base font-semibold text-accent-foreground shadow-lg shadow-accent/25 hover:bg-accent/90" asChild>
                <Link to="/konfigurator">
                  <Settings2 className="mr-2 h-5 w-5" />
                  Konfigurator öffnen
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Link>
              </Button>
              <Button size="lg" variant="outline" className="h-14 px-8 text-base font-semibold" asChild>
                <Link to="/preise">Preise ansehen</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Core Included */}
      <section className="container -mt-8 relative z-10 mb-12">
        <CoreIncluded />
      </section>

      {/* Module Catalog */}
      <section className="container pb-20">
        <div className="rounded-2xl border border-border bg-card shadow-card p-6 lg:p-8">
          <ModuleFilterBar
            search={search}
            onSearchChange={setSearch}
            activeCategory={activeCategory}
            onCategoryChange={setActiveCategory}
            showSelectedOnly={showSelectedOnly}
            onShowSelectedOnlyChange={setShowSelectedOnly}
          />

          <div className="mt-6 space-y-8">
            {Object.entries(grouped).map(([cat, items]) => (
              <div key={cat}>
                {activeCategory === ALL_TAB && (
                  <h3 className="font-display font-semibold text-sm text-muted-foreground uppercase tracking-wider mb-3">
                    {MODULE_CATEGORY_LABELS[cat as ModuleCategory] ?? cat}
                  </h3>
                )}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
                  {items.map((m) => (
                    <ModuleCard key={m.id} module={m} disabled />
                  ))}
                </div>
              </div>
            ))}

            {filtered.length === 0 && (
              <p className="text-center text-sm text-muted-foreground py-10">
                Keine Module gefunden – Filter anpassen oder Suche leeren.
              </p>
            )}
          </div>
        </div>
      </section>

      {/* Example Setups */}
      <section className="bg-secondary/30 py-20 lg:py-28">
        <div className="container">
          <div className="mx-auto max-w-5xl">
            <div className="mb-12 text-center">
              <h2 className="mb-4 font-display text-2xl font-bold text-foreground sm:text-3xl lg:text-4xl">
                Beispiel-Setups
              </h2>
              <p className="text-muted-foreground lg:text-lg">
                Typische Konfigurationen als Startpunkt – im Konfigurator frei anpassbar.
              </p>
            </div>

            <div className="grid gap-6 sm:grid-cols-3">
              {exampleSetups.map((setup) => {
                const preset = presets.find((p) => p.id === setup.presetId);
                const plan = preset ? getPresetPlan(preset) : null;
                const pts = preset ? calcUsedPoints(preset.moduleIds) : 0;

                return (
                  <div
                    key={setup.presetId}
                    className="group relative overflow-hidden rounded-2xl border border-border bg-card p-6 transition-all duration-300 hover:-translate-y-1 hover:border-primary/30 hover:shadow-lg"
                  >
                    <h3 className="mb-1 font-display text-lg font-bold text-foreground">{setup.label}</h3>
                    <p className="mb-4 text-sm text-muted-foreground">{setup.description}</p>

                    <div className="mb-4 flex items-center gap-2">
                      <span className="inline-flex items-center rounded-lg bg-primary/10 px-2 py-0.5 text-xs font-bold text-primary">
                        {pts} Punkte
                      </span>
                      {plan && (
                        <span className="text-xs text-muted-foreground">
                          → {plan.name} ({formatPoints(plan.pointsIncluded)})
                        </span>
                      )}
                    </div>

                    <Button size="sm" className="w-full gap-2" asChild>
                      <Link to={`/konfigurator?preset=${setup.presetId}`}>
                        Im Konfigurator laden
                        <ArrowRight className="h-4 w-4" />
                      </Link>
                    </Button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-background py-20 lg:py-28">
        <div className="container">
          <div className="mx-auto max-w-3xl">
            <div className="mb-12 text-center">
              <h2 className="mb-4 font-display text-2xl font-bold text-foreground sm:text-3xl lg:text-4xl">
                Häufig gestellte Fragen
              </h2>
            </div>

            <Accordion type="single" collapsible className="space-y-4">
              {faqItems.map((item, index) => (
                <AccordionItem
                  key={index}
                  value={`faq-${index}`}
                  className="rounded-2xl border border-border bg-card px-6 data-[state=open]:border-primary/30"
                >
                  <AccordionTrigger className="py-5 text-left font-display text-base font-semibold text-foreground hover:no-underline">
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent className="pb-5 text-muted-foreground">
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>
    </PageLayout>
  );
};

export default Features;
